import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Confetti from 'react-confetti';
import { Check, Twitter, Calculator, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACK_INFO = {
  basic_math:    { name: 'Basic Math Pack',              includes: ['+', '−', '1', '2', '3', '4', '5'], upsell: 'advanced_ops',  upsellName: 'Advanced Operations Bundle',       upsellPrice: '$2.99', price: '$0.99' },
  advanced_ops:  { name: 'Advanced Operations Bundle',   includes: ['×', '÷', '6', '7', '8', '9', '.'], upsell: 'ultimate_math', upsellName: 'Ultimate Mathematician Edition',    upsellPrice: '$9.99', price: '$2.99' },
  ultimate_math: { name: 'Ultimate Mathematician Edition', includes: ['=', 'C', '(', ')'],               upsell: null,            price: '$9.99' },
  negative_numbers: { name: 'Negative Numbers Expansion Pack™', includes: ['Minus sign (as sign)', 'Correct negative results', 'Red − badge'], upsell: null, price: '$4.99' },
  dark_mode_skin:{ name: 'Dark Mode Skin',               includes: ['Dark Calculator Theme'],            upsell: null,            price: '$0.99' },
  sound_effects: { name: 'Sound Effects Pack',           includes: ['Click Sounds', 'Victory Chord'],   upsell: null,            price: '$0.79' },
  history_log:   { name: 'History Log',                  includes: ['100 Calculation History'],          upsell: null,            price: '$1.99' },
  bundle_all:    { name: 'The Full Bundle',               includes: ['Basic Math', 'Advanced Ops', 'Ultimate'], upsell: null,   price: '$13.97' },
};

const CORE_PACKS = ['basic_math', 'advanced_ops', 'ultimate_math'];

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [packData, setPackData] = useState(null);
  const [confettiActive, setConfettiActive] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const attempts = useRef(0);
  const hasSucceeded = useRef(false);
  const consecutiveAuth = useRef(0);

  const [achievementUnlocked, setAchievementUnlocked] = useState(false);

  useEffect(() => {
    const onResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const startPolling = useCallback(() => {
    attempts.current = 0;
    consecutiveAuth.current = 0;
    hasSucceeded.current = false;
    setStatus('checking');

    const poll = async () => {
      if (hasSucceeded.current) return;
      if (attempts.current >= 20) { setStatus('error'); return; }

      attempts.current += 1;
      // Progressive delay: attempts 1-6 → 2s, 7-12 → 4s, 13+ → 6s
      const delay = attempts.current <= 6 ? 2000 : attempts.current <= 12 ? 4000 : 6000;

      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: 'include' });

        if (res.status === 401) {
          consecutiveAuth.current += 1;
          // After 4 consecutive 401s, tell user to log in and keep retrying silently
          if (consecutiveAuth.current >= 4) { setStatus('auth-needed'); }
          setTimeout(poll, delay);
          return;
        }

        consecutiveAuth.current = 0;

        if (!res.ok) { setTimeout(poll, delay); return; }

        const data = await res.json();
        if (data.payment_status === 'paid') {
          hasSucceeded.current = true;
          const info = PACK_INFO[data.pack_id] || { name: data.pack_name, includes: [] };
          setPackData({ ...info, pack_id: data.pack_id });
          setStatus('success');
          setConfettiActive(true);
          const updatedUser = await refreshUser();
          const owned = updatedUser?.unlocked_packs || [];
          if (CORE_PACKS.every(p => owned.includes(p))) setAchievementUnlocked(true);
          setTimeout(() => setConfettiActive(false), 7000);
        } else if (data.status === 'expired') {
          setStatus('error');
        } else {
          setTimeout(poll, delay);
        }
      } catch {
        setTimeout(poll, delay);
      }
    };

    poll();
  }, [sessionId, refreshUser]);

  useEffect(() => {
    if (!sessionId) { navigate('/store', { replace: true }); return; }
    startPolling();
  }, [sessionId, navigate, startPolling]);

  const handleShare = () => {
    const text = `I just paid ${packData?.price || '$0.99'} to use a calculator. Thanks EA. calculatorbyea.com`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Helmet>
        <title>Payment Successful — CalcByEA™</title>
        <meta name="description" content="Your Premium Math Features™ have been unlocked. Welcome to the elite tier of CalcByEA." />
        <link rel="canonical" href="https://calculatorbyea.com/success" />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Payment Successful — CalcByEA™" />
        <meta property="og:description" content="Your Premium Math Features™ have been unlocked." />
        <meta property="og:url" content="https://calculatorbyea.com/success" />
      </Helmet>
      <Navbar />
      {confettiActive && (
        <Confetti
          width={windowSize.width} height={windowSize.height}
          colors={['#09090B', '#3F3F46', '#71717A', '#D4D4D8', '#FAFAFA']}
          numberOfPieces={200} gravity={0.1} recycle={false}
        />
      )}

      <div className="pt-14 min-h-screen flex items-center justify-center px-6">

        {/* CHECKING */}
        {status === 'checking' && (
          <div className="text-center" data-testid="success-checking">
            <div className="font-bebas text-5xl text-zinc-950 uppercase tracking-widest mb-4 animate-pulse">Processing</div>
            <div className="flex gap-1.5 justify-center mb-4">
              {[0,1,2].map(i => <div key={i} className="w-2 h-2 bg-zinc-950 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />)}
            </div>
            <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Activating Premium Math Features™</p>
          </div>
        )}

        {/* SUCCESS */}
        {status === 'success' && packData && (
          <div className="w-full max-w-md" data-testid="success-screen">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-6 h-0.5 bg-zinc-950" />
              <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400">Purchase Complete</span>
            </div>

            <h1 className="font-bebas text-5xl text-zinc-950 leading-tight mb-2">Congratulations.</h1>
            <p className="text-zinc-500 text-sm mb-8">Welcome to the elite tier of Premium Mathematics™.</p>

            {/* Achievement badge */}
            {achievementUnlocked && (
              <div className="border-2 border-zinc-950 bg-zinc-950 p-5 mb-5 shadow-[6px_6px_0px_0px_rgba(9,9,11,0.3)]" data-testid="achievement-badge">
                <div className="text-[9px] font-bold uppercase tracking-[0.4em] text-zinc-500 mb-1">Achievement Unlocked</div>
                <div className="font-bebas text-3xl text-white leading-tight">Full Mathematician™</div>
                <p className="text-zinc-500 text-xs mt-1">You own all 3 core packs. You can now do basic arithmetic. EA is proud of you.</p>
              </div>
            )}

            {/* What was unlocked */}
            <div className="border-2 border-zinc-950 bg-white p-6 mb-5 shadow-[6px_6px_0px_0px_rgba(9,9,11,1)]">
              <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-3">You Unlocked</div>
              <h3 className="font-bebas text-3xl text-zinc-950 mb-4">{packData.name}</h3>
              <div className="flex flex-wrap gap-1.5">
                {packData.includes.map(item => (
                  <div key={item} className="flex items-center gap-1.5 bg-zinc-950 text-white text-xs px-3 py-1.5 font-mono font-bold">
                    <Check size={10} strokeWidth={3} /> {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Upsell */}
            {packData.upsell && (
              <div className="border-2 border-zinc-200 bg-white p-5 mb-5">
                <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-2">Special Offer</div>
                <p className="text-zinc-950 text-sm font-semibold mb-1">You're 1 pack away from {packData.upsellName}.</p>
                <p className="text-zinc-400 text-xs mb-4">Unlock even more Premium Math Features™ now.</p>
                <button
                  onClick={() => navigate('/store')}
                  className="flex items-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-xs px-5 py-2.5 border-2 border-zinc-950 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] transition-all duration-100"
                  data-testid="upsell-btn"
                >
                  Get {packData.upsellName} — {packData.upsellPrice} <ArrowRight size={12} strokeWidth={3} />
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleShare}
                className="flex-1 flex items-center justify-center gap-2 border-2 border-zinc-200 text-zinc-500 py-3.5 text-xs font-bold uppercase tracking-widest hover:border-zinc-950 hover:text-zinc-950 transition-all duration-100"
                data-testid="share-twitter-btn"
              >
                <Twitter size={13} strokeWidth={2.5} /> Share on X
              </button>
              <button
                onClick={() => navigate('/calculator')}
                className="flex-1 flex items-center justify-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-xs py-3.5 border-2 border-zinc-950 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-all duration-100"
                data-testid="go-to-calculator-btn"
              >
                <Calculator size={13} strokeWidth={2.5} /> Use Calculator
              </button>
            </div>
            <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-widest mt-6 text-center">
              EA is a trademark of Electronic Arts. This is a parody. No refunds.
            </p>
          </div>
        )}

        {/* AUTH NEEDED */}
        {status === 'auth-needed' && (
          <div className="text-center max-w-sm" data-testid="success-auth-needed">
            <div className="font-bebas text-5xl text-zinc-950 uppercase mb-3 leading-tight">Log In To<br />Claim Your Pack</div>
            <p className="text-zinc-500 text-sm mb-2">Your payment went through. Log in to unlock your buttons.</p>
            <p className="text-zinc-400 text-xs mb-8">We'll keep checking in the background once you're signed in.</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  const redirectUrl = window.location.href;
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="bg-zinc-950 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 border-2 border-zinc-950 shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[8px_8px_0px_0px_rgba(9,9,11,1)] transition-all duration-100"
                data-testid="auth-needed-login-btn"
              >
                Log In to Claim
              </button>
            </div>
          </div>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <div className="text-center max-w-sm" data-testid="success-error">
            <div className="font-bebas text-5xl text-zinc-950 uppercase mb-3">Transaction Error</div>
            <p className="text-zinc-500 text-sm mb-2">We couldn't confirm your payment automatically.</p>
            <p className="text-zinc-400 text-xs mb-8">If your card was charged, your unlock is waiting — try again or contact us with your email.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={startPolling}
                className="flex items-center gap-2 border-2 border-zinc-950 text-zinc-950 font-bold uppercase tracking-widest text-sm px-6 py-3 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] transition-all duration-100"
                data-testid="retry-poll-btn"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/store')}
                className="bg-zinc-950 text-white font-bold uppercase tracking-widest text-sm px-6 py-3 border-2 border-zinc-950 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] transition-all duration-100"
                data-testid="back-to-store-btn"
              >
                Back to Store
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
